import { ref, computed, inject } from 'vue';

export const breakpoints = {
  mobile: 678,
  tablet: 992,
  desktop: Infinity
};

export function getCurrentBreakpoint() {
  const width = window.innerWidth;
  if (width <= breakpoints.mobile) return 'mobile';
  if (width <= breakpoints.tablet) return 'tablet';
  return 'desktop';
}

export function isMobile() {
  return getCurrentBreakpoint() === 'mobile';
}

export function isDesktop() {
  return getCurrentBreakpoint() === 'desktop';
}

// Global reactive state for the current breakpoint
const globalBreakpoint = ref(getCurrentBreakpoint());

export const mqPlugin = {
  install(app) {
    // Update on window resize with debouncing
    let resizeTimeout;
    const updateBreakpoint = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const newBreakpoint = getCurrentBreakpoint();
        if (globalBreakpoint.value !== newBreakpoint) {
          globalBreakpoint.value = newBreakpoint;
        }
      }, 150); // Debounce resize events
    };
    
    // Make $mq reactive by adding it as a computed property to all component instances
    app.mixin({
      computed: {
        $mq() {
          // This computed property will automatically re-evaluate when globalBreakpoint changes
          return globalBreakpoint.value;
        }
      }
    });
    
    // Set up event listeners for window resize
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateBreakpoint);
      // Also listen for orientation change on mobile devices
      window.addEventListener('orientationchange', () => {
        setTimeout(updateBreakpoint, 100);
      });
    }
    
    // Provide reactive breakpoint state for Composition API components
    app.provide('currentBreakpoint', globalBreakpoint);
  }
};

// Composable for Vue 3 Composition API (optional, for new components)
export function useBreakpoints() {
  const currentBreakpoint = inject('currentBreakpoint', globalBreakpoint);
  
  return {
    currentBreakpoint: computed(() => currentBreakpoint.value),
    isMobile: computed(() => currentBreakpoint.value === 'mobile'),
    isTablet: computed(() => currentBreakpoint.value === 'tablet'), 
    isDesktop: computed(() => currentBreakpoint.value === 'desktop'),
    breakpoints: Object.freeze(breakpoints)
  };
}
